import toast from 'react-hot-toast';
import GraphService from './graphService';

/**
 * Enhanced Authentication service for Microsoft SSO integration with user management
 */
export class AuthService {
    static instance = null;
    static msalInstance = null;
    static currentAccount = null;
    static userProfile = null;
    static refreshAttempts = 0;
    static maxRefreshAttempts = 3;
    static isInitialized = false;
    static sessionChecked = false;
    
    // Prevent duplicate login/logout recording
    static isRecordingLogin = false;
    static isRecordingLogout = false;

    /**
     * Initialize authentication service with MSAL instance only
     * First phase initialization for login operations
     */
    static initializeMsal(msalInstance) {
        try {
            if (!msalInstance) {
                throw new Error('MSAL instance is required for initialization');
            }

            AuthService.msalInstance = msalInstance;
            AuthService.refreshAttempts = 0;
            AuthService.isInitialized = true;
            AuthService.sessionChecked = false;

        } catch (error) {
            throw new Error(`AuthService MSAL initialization failed: ${error.message}`);
        }
    }

    /**
     * Initialize user session with user existence check and creation
     */
    static async initializeUserSession(account, existingUserProfile = null) {
        try {
            if (!AuthService.isInitialized) {
                throw new Error('AuthService MSAL must be initialized first');
            }

            if (!account) {
                throw new Error('Account is required for session initialization');
            }

            AuthService.currentAccount = account;

            let userProfile = null;
            
            try {
                const userCheckResult = await AuthService.checkOrCreateUser(account);
                
                // Handle different user status scenarios
                if (userCheckResult.status === 'pending') {
                    // User exists but is pending approval
                    throw new Error(`PENDING_APPROVAL:Your account is pending approval. Please wait for administrator approval.`);
                }
                
                if (userCheckResult.status === 'rejected') {
                    // User exists but was rejected
                    throw new Error(`ACCOUNT_REJECTED:Your account has been rejected. Please contact administrator.`);
                }
                
                if (userCheckResult.status === 'suspended') {
                    // User account is suspended
                    throw new Error(`ACCOUNT_SUSPENDED:Your account has been suspended. Please contact administrator.`);
                }
                
                if (!userCheckResult.success) {
                    // Failed to create or get user
                    throw new Error(`USER_CREATION_FAILED:${userCheckResult.message || 'Failed to create user account'}`);
                }
                
                userProfile = userCheckResult.data;

                // Validate user status and role - only approved users can proceed
                if (!userProfile || userProfile.status !== 'approved') {
                    const status = userProfile?.status || 'unknown';
                    switch (status) {
                        case 'pending':
                            throw new Error(`PENDING_APPROVAL:Your account is pending approval. Please wait for administrator approval.`);
                        case 'rejected':
                            throw new Error(`ACCOUNT_REJECTED:Your account has been rejected. Please contact administrator.`);
                        case 'suspended':
                            throw new Error(`ACCOUNT_SUSPENDED:Your account has been suspended. Please contact administrator.`);
                        default:
                            throw new Error(`ACCOUNT_STATUS_INVALID:Account status is ${status}. Please contact administrator.`);
                    }
                }

            } catch (backendError) {
                console.error('❌ Backend user validation failed:', backendError);
                
                // Check if this is a network/connectivity error and user has valid localStorage session
                const isNetworkError = backendError.message?.includes('Server error') ||
                                     backendError.message?.includes('Network error') ||
                                     backendError.message?.includes('endpoint not found');
                
                if (isNetworkError && AuthService.hasValidStoredSession()) {
                    const email = localStorage.getItem('userEmail');
                    const accountEmail = account.username || account.mail;
                    
                    // If the stored session is for the same user, allow fallback
                    if (email === accountEmail) {
                        console.warn('⚠️ Using localStorage fallback due to backend connectivity issues');
                        
                        // Create user profile from localStorage
                        userProfile = {
                            id: localStorage.getItem('sessionId'),
                            email: email,
                            name: localStorage.getItem('userName'),
                            role: localStorage.getItem('userRole'),
                            status: 'approved', // Assume approved if localStorage session exists
                            clientGroup: 'all'
                        };
                        
                    } else {
                        // Different user - can't use fallback
                        throw backendError;
                    }
                } else {
                    // Not a network error or no valid session - re-throw original error
                    throw backendError;
                }
            }

            AuthService.userProfile = userProfile;
            AuthService.sessionChecked = true;

            // Store session data
            const sessionData = {
                id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                email: userProfile.email,
                name: userProfile.name || userProfile.displayName || account.name,
                role: userProfile.role,
                lastLogin: new Date().toISOString()
            };

            AuthService.storeSessionIfChanged(sessionData);

            //  Record login in audit with duplicate prevention (skip if using fallback)
            if (userProfile.id && userProfile.id !== localStorage.getItem('sessionId')) {
                await AuthService.recordUserLogin(userProfile.id, sessionData.id);
            }

            // Fetch profile photo asynchronously (simplified - no localStorage caching)
            AuthService.fetchAndUpdateProfilePhoto(userProfile.email);

            return userProfile;

        } catch (error) {
            // Re-throw with proper error formatting
            throw error;
        }
    }

    // Track ongoing user creation requests to prevent duplicates
    static pendingUserCreations = new Set();

    /**
     * Check if user exists in database, create if needed
     */
    static async checkOrCreateUser(account) {
        try {
            const email = account.username || account.mail;
            
            if (!email) {
                throw new Error('No email found in Microsoft account');
            }

            // Prevent duplicate user creation requests
            if (AuthService.pendingUserCreations.has(email)) {
                // Wait for ongoing request to complete
                let attempts = 0;
                while (AuthService.pendingUserCreations.has(email) && attempts < 10) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                    attempts++;
                }
                
                // If still pending after 5 seconds, proceed anyway
                if (AuthService.pendingUserCreations.has(email)) {
                    console.warn('⚠️ Timeout waiting for user creation, proceeding...');
                    AuthService.pendingUserCreations.delete(email);
                }
            }

            // Validate API configuration
            const apiBaseUrl = window.ADI_CONFIG?.api?.baseUrl;
            if (!apiBaseUrl) {
                console.error('❌ Missing API configuration:', window.ADI_CONFIG);
                throw new Error('API configuration not found. Please check application configuration.');
            }

            // First, try to get existing user by email
            const getUserUrl = `${apiBaseUrl}/cas/user/email/${encodeURIComponent(email)}`;

            const response = await fetch(getUserUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            if (response.ok) {
                const result = await response.json();
                
                // Handle both "data" and "user" response structures
                const userData = result.data || result.user;
                if (result.success && userData) {
                    // User exists, return user data with proper structure
                    return {
                        success: true,
                        status: userData.status,
                        data: {
                            id: userData.id,
                            email: userData.email,
                            name: userData.displayName || userData.name,
                            displayName: userData.displayName,
                            role: userData.role,
                            status: userData.status,
                            clientGroup: userData.clientGroup
                        }
                    };
                }
            } else {
                // If it's not a 404, it might be a server error
                if (response.status !== 404) {
                    const errorText = await response.text();
                    console.error('❌ Unexpected error checking user:', errorText);
                    throw new Error(`Server error while checking user: ${response.status} ${response.statusText}`);
                }
            }

            // Mark user creation as pending
            AuthService.pendingUserCreations.add(email);

            try {
                // User doesn't exist, create new user
                const microsoftProfile = {
                    id: account.localAccountId || account.homeAccountId,
                    userPrincipalName: account.username,
                    mail: email,
                    displayName: account.name || email.split('@')[0],
                    givenName: account.name?.split(' ')[0] || '',
                    surname: account.name?.split(' ').slice(1).join(' ') || ''
                };

                const createUserUrl = `${apiBaseUrl}/cas/user/authenticate`;

                const createRequestBody = {
                    microsoftProfile: microsoftProfile,
                    ipAddress: 'unknown', // Could be enhanced to get real IP
                    userAgent: navigator.userAgent || 'unknown'
                };

                const createResponse = await fetch(createUserUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: JSON.stringify(createRequestBody)
                });

                if (!createResponse.ok) {
                    const errorText = await createResponse.text();
                    console.error('❌ Create user API failed:', {
                        status: createResponse.status,
                        statusText: createResponse.statusText,
                        error: errorText,
                        url: createUserUrl,
                        requestBody: createRequestBody
                    });
                    
                    // Provide more specific error messages
                    if (createResponse.status === 500) {
                        throw new Error(`Server error during user creation. Please contact administrator. (${createResponse.status})`);
                    } else if (createResponse.status === 404) {
                        throw new Error(`User creation endpoint not found. Please contact administrator. (${createUserUrl})`);
                    } else if (createResponse.status >= 400 && createResponse.status < 500) {
                        throw new Error(`User creation request failed: ${errorText || createResponse.statusText}`);
                    } else {
                        throw new Error(`Network error during user creation: ${createResponse.status} ${createResponse.statusText}`);
                    }
                }

                const createResult = await createResponse.json();
                
                if (!createResult.success) {
                    console.warn('⚠️ User creation returned success=false:', createResult);
                    
                    // Handle different response scenarios from backend
                    if (createResult.status === 'pending') {
                        return {
                            success: false,
                            status: 'pending',
                            message: createResult.message || 'Your account is pending approval. Please wait for administrator approval.'
                        };
                    }
                    
                    // If user creation failed, check if user now exists (might be duplicate request)
                    if (createResult.error === 'USER_CREATION_FAILED' || createResult.message?.includes('already exists')) {
                        // Wait a moment and try to get the user again
                        await new Promise(resolve => setTimeout(resolve, 500));
                        
                        try {
                            const recheckResponse = await fetch(getUserUrl, {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-Requested-With': 'XMLHttpRequest'
                                }
                            });
                            
                            if (recheckResponse.ok) {
                                const recheckResult = await recheckResponse.json();
                                const userData = recheckResult.data || recheckResult.user;
                                
                                if (recheckResult.success && userData) {
                                    return {
                                        success: true,
                                        status: userData.status,
                                        data: {
                                            id: userData.id,
                                            email: userData.email,
                                            name: userData.displayName || userData.name,
                                            displayName: userData.displayName,
                                            role: userData.role,
                                            status: userData.status,
                                            clientGroup: userData.clientGroup
                                        }
                                    };
                                }
                            }
                        } catch (recheckError) {
                            console.warn('⚠️ Recheck failed:', recheckError);
                            // Continue to original error handling
                        }
                    }
                    
                    return {
                        success: false,
                        status: createResult.status || 'error',
                        message: createResult.message || 'Failed to create user account'
                    };
                }

                // User created successfully - the user data is directly under createResult.data
                const userData = createResult.data;
                
                if (!userData) {
                    throw new Error('User creation succeeded but no user data returned');
                }
                
                return {
                    success: true,
                    status: userData?.status || 'approved', // Use actual status from response
                    data: {
                        id: userData.id,
                        email: userData.email,
                        name: userData.displayName || userData.name,
                        displayName: userData.displayName,
                        role: userData.role || 'user',
                        status: userData.status || 'approved',
                        clientGroup: userData.clientGroup || 'all'
                    }
                };

            } finally {
                // Always remove from pending set
                AuthService.pendingUserCreations.delete(email);
            }

        } catch (error) {
            console.error('❌ Error in checkOrCreateUser:', error);
            
            // Enhanced error context for debugging
            console.error('🔍 Error context:', {
                email: account.username || account.mail,
                accountData: account,
                apiBaseUrl: window.ADI_CONFIG?.api?.baseUrl,
                fullConfig: window.ADI_CONFIG,
                timestamp: new Date().toISOString()
            });
            
            // Ensure we remove from pending set on error
            const email = account.username || account.mail;
            if (email) {
                AuthService.pendingUserCreations.delete(email);
            }
            
            return {
                success: false,
                status: 'error',
                message: `User account validation failed: ${error.message}`
            };
        }
    }

    /**
     * Record user login for audit purposes - prevent duplicates
     * Creates audit trail for login events
     */
    static async recordUserLogin(userId, sessionId) {
        // Prevent duplicate recording
        if (AuthService.isRecordingLogin) {
            return;
        }
        
        try {
            AuthService.isRecordingLogin = true;
            
            const response = await fetch(`${window.ADI_CONFIG?.api?.baseUrl}/cas/user/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    userId: userId,
                    sessionId: sessionId
                })
            });

            if (!response.ok) {
                console.warn('Failed to record login audit');
            }
        } catch (error) {
            console.warn('Failed to record login audit:', error);
        } finally {
            AuthService.isRecordingLogin = false;
        }
    }

    /**
     * Record user logout for audit purposes - prevent duplicates
     * Creates audit trail for logout events
     */
    static async recordUserLogout(userId, sessionId) {
        // Prevent duplicate recording
        if (AuthService.isRecordingLogout) {
            return;
        }
        
        try {
            AuthService.isRecordingLogout = true;
            
            const response = await fetch(`${window.ADI_CONFIG?.api?.baseUrl}/cas/user/logout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    userId: userId,
                    sessionId: sessionId
                })
            });

            if (!response.ok) {
                console.warn('Failed to record logout audit');
            }
        } catch (error) {
            console.warn('Failed to record logout audit:', error);
        } finally {
            AuthService.isRecordingLogout = false;
        }
    }

    /**
     *  Fetch user profile photo from Microsoft Graph
     */
    static async fetchAndUpdateProfilePhoto(userEmail) {
        try {
            // Check if photo already exists in userProfileImage
            const existingPhoto = localStorage.getItem('userProfileImage');
            if (existingPhoto) {
                AuthService.updateUserProfilePhoto(existingPhoto);
                return; // Use existing photo, don't fetch again
            }

            // Fetch photo from Microsoft Graph
            const photoDataUrl = await GraphService.getUserProfilePhotoWithFallback(userEmail);
            
            if (photoDataUrl) {
                AuthService.updateUserProfilePhoto(photoDataUrl);
            } else {
                AuthService.updateUserProfilePhoto(null);
            }

        } catch (error) {
            AuthService.updateUserProfilePhoto(null);
        }
    }

    /**
     * Update user profile with photo
     * Only uses userProfileImage localStorage key
     */
    static updateUserProfilePhoto(photoDataUrl) {
        try {
            if (AuthService.userProfile) {
                AuthService.userProfile = {
                    ...AuthService.userProfile,
                    profileImage: photoDataUrl
                };

                if (photoDataUrl) {
                    localStorage.setItem('userProfileImage', photoDataUrl);
                } else {
                    localStorage.removeItem('userProfileImage');
                }
                
                window.dispatchEvent(new CustomEvent('userProfilePhotoUpdated', {
                    detail: { 
                        email: AuthService.userProfile.email,
                        profileImage: photoDataUrl 
                    }
                }));
            }
        } catch (error) {
            // Silently handle profile photo update errors
        }
    }

    /**
     * Clear user session while keeping MSAL instance - prevent duplicate recording
     * Removed profilePhoto_ localStorage cleanup and pending user creation cleanup
     */
    static clearUserSession() {
        try {
            // Reset recording flags
            AuthService.isRecordingLogin = false;
            AuthService.isRecordingLogout = false;
            
            // Clear pending user creations
            AuthService.pendingUserCreations.clear();
            
            AuthService.currentAccount = null;
            AuthService.userProfile = null;
            AuthService.refreshAttempts = 0;
            AuthService.sessionChecked = false;

            // Clear only essential session items (removed profilePhoto_ cleanup)
            AuthService.clearStorageItems([
                'userEmail', 'userRole', 'userName', 'loginTime', 'sessionId', 'userProfileImage'
            ]);

            GraphService.clearCache();

        } catch (error) {
            // Silently handle session clear errors
        }
    }

    /**
     * Clear MSAL cache from storage
     * Removes all msal.* keys to prevent auto re-login
     */
    static clearMsalCache() {
        try {
            // Clear from both sessionStorage and localStorage
            const storages = [sessionStorage, localStorage];
            
            storages.forEach(storage => {
                const msalKeys = Object.keys(storage).filter(key => key.startsWith('msal.'));
                msalKeys.forEach(key => {
                    try {
                        storage.removeItem(key);
                    } catch (error) {
                        // Silently handle removal errors
                    }
                });
            });
        } catch (error) {
            // Silently handle cache clear errors
        }
    }

    /**
     * Store session data only if different from current
     */
    static storeSessionIfChanged(userInfo) {
        try {
            const currentEmail = localStorage.getItem('userEmail');
            const currentSessionId = localStorage.getItem('sessionId');
            
            if (currentEmail === userInfo.email && currentSessionId) {
                return false;
            }

            localStorage.setItem('userEmail', userInfo.email);
            localStorage.setItem('userRole', userInfo.role);
            localStorage.setItem('userName', userInfo.name);
            localStorage.setItem('loginTime', userInfo.lastLogin);
            localStorage.setItem('sessionId', userInfo.id);

            return true;

        } catch (error) {
            return false;
        }
    }

    /**
     * Optimized storage clearing utility
     */
    static clearStorageItems(keys) {
        keys.forEach(key => {
            try {
                if (localStorage.getItem(key) !== null) {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                // Silently handle storage removal errors
            }
        });
    }

    /**
     * Check if session data exists and is valid
     */
    static hasValidStoredSession() {
        try {
            const userEmail = localStorage.getItem('userEmail');
            const userRole = localStorage.getItem('userRole');
            const sessionId = localStorage.getItem('sessionId');

            if (!userEmail || !userRole || !sessionId) {
                return false;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(userEmail)) {
                return false;
            }

            return true;

        } catch (error) {
            return false;
        }
    }

    /**
     * initialize method for backward compatibility
     */
    static initialize(msalInstance, account, userProfile) {
        if (!AuthService.isInitialized) {
            AuthService.initializeMsal(msalInstance);
        }
        
        if (account && userProfile) {
            AuthService.initializeUserSession(account, userProfile);
        }
    }

    /**
     * Initiate Microsoft SSO login using popup with redirect fallback
     */
    static async login() {
        try {
            if (!AuthService.isInitialized || !AuthService.msalInstance) {
                throw new Error('AuthService MSAL not initialized. Cannot perform login.');
            }

            const defaultScopes = ['User.Read', 'User.ReadBasic.All', 'openid', 'profile', 'email'];
            const configuredScopes = window.ADI_CONFIG?.msal?.scopes || defaultScopes;
            
            const loginRequest = {
                scopes: configuredScopes,
                prompt: 'select_account'
            };

            // Detect mobile device - skip popup on mobile
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            
            if (isMobile) {
                // Mobile devices - use redirect directly
                await AuthService.msalInstance.loginRedirect(loginRequest);
                return null;
            }

            // Desktop - try popup first (better UX)
            try {
                const loginResponse = await AuthService.msalInstance.loginPopup(loginRequest);
                
                if (loginResponse) {
                    AuthService.msalInstance.setActiveAccount(loginResponse.account);
                    AuthService.currentAccount = loginResponse.account;
                    return loginResponse;
                }
            } catch (popupError) {
                console.log('Popup login failed, trying redirect:', popupError.errorCode || popupError.message);
                
                // Popup failed (blocked, incognito, COOP policy) - use redirect
                if (popupError.errorCode === 'popup_window_error' || 
                    popupError.errorCode === 'empty_window_error' ||
                    popupError.errorCode === 'monitor_window_timeout' ||
                    popupError.errorMessage?.includes('popup') ||
                    popupError.message?.includes('Cross-Origin-Opener-Policy') ||
                    popupError.message?.includes('window.closed')) {
                    await AuthService.msalInstance.loginRedirect(loginRequest);
                    return null;
                }
                
                // Re-throw other errors
                throw popupError;
            }

        } catch (error) {
            if (error.errorCode === 'user_cancelled') {
                toast('Login cancelled by user', {
                    icon: '❌',
                    duration: 3000,
                    position: 'bottom-center'
                });
            } else {
                toast.error('Login failed. Please try again.', {
                    duration: 5000,
                    position: 'bottom-center'
                });
            }
            
            throw new Error(`Login failed: ${error.message}`);
        }
    }

    /**
     * Handle successful login callback with enhanced user processing
     */
    static async handleLoginSuccess(loginResponse = null) {
        try {
            if (!AuthService.msalInstance) {
                throw new Error('MSAL instance not available for login callback');
            }

            let account = loginResponse?.account;
            
            if (!account) {
                const accounts = AuthService.msalInstance.getAllAccounts();
                
                if (accounts.length === 0) {
                    throw new Error('No authenticated accounts found after login');
                }
                
                account = accounts[0];
            }

            AuthService.currentAccount = account;
            AuthService.msalInstance.setActiveAccount(account);

            // Check or create user and initialize session
            const userProfile = await AuthService.initializeUserSession(account);

            return { account, userProfile };

        } catch (error) {
            // Re-throw to let caller handle specific error types
            throw error;
        }
    }

    /**
     * Enhanced logout with audit recording - prevent duplicates
     */
    static async logout(showToast = true) {
        try {
            // Set flag in localStorage to prevent auto re-login (persists through redirect)
            localStorage.setItem('isLoggingOut', 'true');
            
            // Record logout audit before clearing session - only once
            if (AuthService.userProfile && !AuthService.isRecordingLogout) {
                const sessionId = localStorage.getItem('sessionId');
                await AuthService.recordUserLogout(AuthService.userProfile.id, sessionId);
            }

            // Clear application session
            AuthService.clearUserSession();

            // Clear MSAL account data from localStorage
            AuthService.clearMsalCache();

            try {
                sessionStorage.clear();
            } catch (storageError) {
                // Silently handle sessionStorage errors
            }

            if (AuthService.msalInstance && AuthService.currentAccount) {
                const logoutRequest = {
                    account: AuthService.currentAccount,
                    postLogoutRedirectUri: window.ADI_CONFIG?.msal?.auth?.postLogoutRedirectUri || window.ADI_CONFIG?.msal?.auth?.redirectUri
                };

                await AuthService.msalInstance.logoutRedirect(logoutRequest);
            } else {
                // Use configured URLs with fallback to redirectUri to preserve subdirectory
                const logoutUrl = window.ADI_CONFIG?.msal?.auth?.forceLogoutURL || 
                                 window.ADI_CONFIG?.msal?.auth?.postLogoutRedirectUri || 
                                 window.ADI_CONFIG?.msal?.auth?.redirectUri || 
                                 window.location.origin;
                window.location.href = logoutUrl;
            }

        } catch (error) {
            AuthService.forceLogout();
        }
    }

    /**
     * Force logout without MSAL logout
     */
    static forceLogout() {
        try {
            // Clear all storage
            localStorage.clear();
            sessionStorage.clear();
            
            // Set logout flag after clearing
            localStorage.setItem('isLoggingOut', 'true');
            
            AuthService.currentAccount = null;
            AuthService.userProfile = null;
            AuthService.refreshAttempts = 0;
            AuthService.sessionChecked = false;
            
            // Reset recording flags
            AuthService.isRecordingLogin = false;
            AuthService.isRecordingLogout = false;
            
            setTimeout(() => {
                window.location.href = window.location.origin;
            }, 100);

        } catch (error) {
            window.location.reload();
        }
    }

    /**
     * Get current access token
     */
    static async getAccessToken() {
        try {
            if (!AuthService.msalInstance || !AuthService.currentAccount) {
                throw new Error('Authentication not initialized or no account available');
            }

            const defaultScopes = ['User.Read'];
            const configuredScopes = window.ADI_CONFIG?.msal?.scopes || defaultScopes;
            
            const tokenRequest = {
                scopes: configuredScopes,
                account: AuthService.currentAccount,
                forceRefresh: false
            };

            const response = await AuthService.msalInstance.acquireTokenSilent(tokenRequest);
            
            AuthService.refreshAttempts = 0;
            
            return response.accessToken;

        } catch (error) {
            return AuthService.acquireTokenInteractive();
        }
    }

    /**
     * Acquire token through interactive flow
     */
    static async acquireTokenInteractive() {
        try {
            if (!AuthService.msalInstance || !AuthService.currentAccount) {
                throw new Error('MSAL instance or account not available for interactive token acquisition');
            }

            const defaultScopes = ['User.Read'];
            const configuredScopes = window.ADI_CONFIG?.msal?.scopes || defaultScopes;
            
            const tokenRequest = {
                scopes: configuredScopes,
                account: AuthService.currentAccount,
                prompt: 'select_account'
            };

            try {
                const response = await AuthService.msalInstance.acquireTokenPopup(tokenRequest);
                return response.accessToken;
            } catch (popupError) {
                await AuthService.msalInstance.acquireTokenRedirect(tokenRequest);
                return null;
            }

        } catch (error) {
            throw new Error(`Token acquisition failed: ${error.message}`);
        }
    }

    /**
     * Refresh access token
     */
    static async refreshToken() {
        try {
            if (AuthService.refreshAttempts >= AuthService.maxRefreshAttempts) {
                throw new Error('Maximum refresh attempts exceeded');
            }

            AuthService.refreshAttempts++;

            const defaultScopes = ['User.Read'];
            const configuredScopes = window.ADI_CONFIG?.msal?.scopes || defaultScopes;
            
            const tokenRequest = {
                scopes: configuredScopes,
                account: AuthService.currentAccount,
                forceRefresh: true
            };

            const response = await AuthService.msalInstance.acquireTokenSilent(tokenRequest);
            
            AuthService.refreshAttempts = 0;
            
            return response.accessToken;

        } catch (error) {
            if (AuthService.refreshAttempts >= AuthService.maxRefreshAttempts) {
                toast.error('Session expired. Please login again.', {
                    duration: 5000,
                    position: 'bottom-center'
                });
                
                AuthService.forceLogout();
                throw new Error('Session expired');
            }
            
            const delay = Math.pow(2, AuthService.refreshAttempts) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
            
            throw error;
        }
    }

    /**
     * Check if user is authenticated
     */
    static isAuthenticated() {
        try {
            if (!AuthService.msalInstance) {
                return false;
            }

            const accounts = AuthService.msalInstance.getAllAccounts();
            const hasStoredSession = AuthService.hasValidStoredSession();
            
            const isValid = accounts.length > 0 && hasStoredSession;

            return isValid;

        } catch (error) {
            return false;
        }
    }

    /**
     * Get current user profile with enhanced role information
     */
    static getCurrentUser() {
        try {
            if (AuthService.userProfile) {
                return AuthService.userProfile;
            }

            if (!AuthService.hasValidStoredSession()) {
                return null;
            }

            const email = localStorage.getItem('userEmail');
            const role = localStorage.getItem('userRole');
            const name = localStorage.getItem('userName');
            const loginTime = localStorage.getItem('loginTime');
            const sessionId = localStorage.getItem('sessionId');
            const profileImage = localStorage.getItem('userProfileImage');

            const userProfile = {
                email,
                role,
                name: name || email.split('@')[0] || 'User',
                id: sessionId,
                lastLogin: loginTime || new Date().toISOString(),
                profileImage: profileImage || null
            };

            AuthService.userProfile = userProfile;
            
            // Only fetch photo if we don't have one
            if (email && !profileImage) {
                AuthService.fetchAndUpdateProfilePhoto(email);
            }
            
            return userProfile;

        } catch (error) {
            return null;
        }
    }

    /**
     * Check if current user has admin role
     */
    static isAdmin() {
        try {
            const user = AuthService.getCurrentUser();
            return user?.role === 'admin';
        } catch (error) {
            return false;
        }
    }

    /**
     * Check if current user has user role (Support desk user)
     */
    static isUser() {
        try {
            const user = AuthService.getCurrentUser();
            return user?.role === 'user';
        } catch (error) {
            return false;
        }
    }

    /**
     * Get user's accessible clients
     */
    static getUserClients() {
        try {
            const user = AuthService.getCurrentUser();
            
            if (user?.role === 'admin') {
                return ['all'];
            }
            
            return [];
        } catch (error) {
            return [];
        }
    }

    /**
     * Handle authentication errors with specific error types
     */
    static handleAuthError(error) {
        const errorMessage = error.message || error.toString();
        
        // Handle custom error types from user creation/validation
        if (errorMessage.includes('PENDING_APPROVAL:')) {
            const message = errorMessage.split('PENDING_APPROVAL:')[1];
            toast('Account Pending Approval', {
                icon: '⏳',
                duration: 8000,
                position: 'bottom-center',
                style: {
                    background: '#f59e0b',
                    color: 'white'
                }
            });
            toast(message, {
                duration: 8000,
                position: 'bottom-center'
            });
            return 'PENDING_APPROVAL';
        }
        
        if (errorMessage.includes('ACCOUNT_REJECTED:')) {
            const message = errorMessage.split('ACCOUNT_REJECTED:')[1];
            toast.error('Account Rejected', {
                duration: 10000,
                position: 'bottom-center'
            });
            toast.error(message, {
                duration: 10000,
                position: 'bottom-center'
            });
            return 'ACCOUNT_REJECTED';
        }
        
        if (errorMessage.includes('ACCOUNT_SUSPENDED:')) {
            const message = errorMessage.split('ACCOUNT_SUSPENDED:')[1];
            toast.error('Account Suspended', {
                duration: 10000,
                position: 'bottom-center'
            });
            toast.error(message, {
                duration: 10000,
                position: 'bottom-center'
            });
            return 'ACCOUNT_SUSPENDED';
        }
        
        if (errorMessage.includes('USER_CREATION_FAILED:')) {
            const message = errorMessage.split('USER_CREATION_FAILED:')[1];
            toast.error('Account Creation Failed', {
                duration: 8000,
                position: 'bottom-center'
            });
            toast.error(message, {
                duration: 8000,
                position: 'bottom-center'
            });
            return 'USER_CREATION_FAILED';
        }

        // Handle standard authentication errors
        if (errorMessage.includes('token') || 
            errorMessage.includes('expired') ||
            errorMessage.includes('AADSTS')) {
            toast.error('Session expired. Please login again.', {
                duration: 5000,
                position: 'bottom-center'
            });
            
            AuthService.forceLogout();
            return 'SESSION_EXPIRED';
        }

        if (errorMessage.includes('consent') || 
            errorMessage.includes('permission') ||
            errorMessage.includes('AADSTS65001')) {
            toast.error('Additional permissions required. Please contact administrator.', {
                duration: 6000,
                position: 'bottom-center'
            });
            return 'PERMISSION_REQUIRED';
        }

        if (errorMessage.includes('network') || 
            errorMessage.includes('connection') ||
            errorMessage.includes('fetch')) {
            toast.error('Network error. Please check your internet connection.', {
                duration: 5000,
                position: 'bottom-center'
            });
            return 'NETWORK_ERROR';
        }

        toast.error('Authentication error occurred. Please try again.', {
            duration: 5000,
            position: 'bottom-center'
        });
        
        return 'GENERAL_AUTH_ERROR';
    }

    /**
     * Get session information
     */
    static getSessionInfo() {
        try {
            return {
                isInitialized: AuthService.isInitialized,
                isAuthenticated: AuthService.isAuthenticated(),
                currentUser: AuthService.getCurrentUser(),
                refreshAttempts: AuthService.refreshAttempts,
                hasAccount: !!AuthService.currentAccount,
                hasMsalInstance: !!AuthService.msalInstance,
                hasValidStoredSession: AuthService.hasValidStoredSession(),
                sessionChecked: AuthService.sessionChecked,
                loginTime: localStorage.getItem('loginTime'),
                sessionId: localStorage.getItem('sessionId'),
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            return {
                error: error.message,
                timestamp: new Date().toISOString()
            };
        }
    }

    /**
     * Validate session health
     */
    static validateSession() {
        try {
            if (AuthService.sessionChecked) {
                return { valid: true, issues: [] };
            }

            const sessionInfo = AuthService.getSessionInfo();
            
            const issues = [];
            
            if (sessionInfo.isAuthenticated && !sessionInfo.hasAccount) {
                issues.push('Authenticated but no MSAL account');
            }
            
            if (sessionInfo.hasValidStoredSession && !sessionInfo.currentUser) {
                issues.push('Valid stored session but no user profile');
            }
            
            const result = { valid: issues.length === 0, issues };
            
            if (result.valid) {
                AuthService.sessionChecked = true;
            }
            
            return result;
            
        } catch (error) {
            return { valid: false, issues: ['Validation error'] };
        }
    }
}